import React, { useState, useEffect } from 'react';
import { Card, Table, Button, Tag, Input, Select, message, Modal, Form, InputNumber } from 'antd';
import { SearchOutlined, PlusOutlined } from '@ant-design/icons';
import api from '../../services/api';

const { Search } = Input;
const { Option } = Select;
const { confirm } = Modal;

interface User {
  id: number;
  username: string;
  email: string;
  balance: number;
  role: string;
  status: string;
  created_at: string;
  server_count: number;
}

const Users: React.FC = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [rechargeModalVisible, setRechargeModalVisible] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);

  useEffect(() => {
    loadUsers();
  }, [search, statusFilter]);

  const loadUsers = async () => {
    try {
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      if (statusFilter) params.append('status', statusFilter);
      
      const response = await api.get(`/admin/users?${params.toString()}`);
      setUsers(response.data.users || []);
    } catch (error: any) {
      console.error('加载用户列表失败:', error);
      message.error(`加载用户列表失败: ${error.response?.data?.message || error.message || '未知错误'}`);
      setUsers([]);
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (userId: number, newStatus: string) => {
    try {
      await api.put(`/admin/users/${userId}/status`, { status: newStatus });
      message.success('用户状态更新成功');
      loadUsers();
    } catch (error: any) {
      message.error(error.response?.data?.message || '更新状态失败');
    }
  };

  const handleRecharge = async (values: { amount: number; note: string }) => {
    if (!selectedUser) return;
    
    try {
      await api.post('/payment/recharge', {
        userId: selectedUser.id,
        amount: values.amount,
        note: values.note
      });
      message.success('充值成功');
      setRechargeModalVisible(false);
      setSelectedUser(null);
      loadUsers();
    } catch (error: any) {
      message.error(error.response?.data?.message || '充值失败');
    }
  };

  const getStatusTag = (status: string) => {
    const statusMap = {
      active: { color: 'green', text: '正常' },
      suspended: { color: 'orange', text: '暂停' },
      banned: { color: 'red', text: '封禁' }
    };
    
    const statusInfo = statusMap[status as keyof typeof statusMap] || { color: 'default', text: status };
    return <Tag color={statusInfo.color}>{statusInfo.text}</Tag>;
  };

  const getRoleTag = (role: string) => {
    return role === 'admin' ? (
      <Tag color="red">管理员</Tag>
    ) : (
      <Tag color="blue">普通用户</Tag>
    );
  };

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 60,
    },
    {
      title: '用户名',
      dataIndex: 'username',
      key: 'username',
      width: 120,
    },
    {
      title: '邮箱',
      dataIndex: 'email',
      key: 'email',
      width: 200,
    },
    {
      title: '角色',
      dataIndex: 'role',
      key: 'role',
      width: 100,
      render: (role: string) => getRoleTag(role),
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (status: string) => getStatusTag(status),
    },
    {
      title: '余额',
      dataIndex: 'balance',
      key: 'balance',
      width: 100,
      render: (balance: any) => `¥${(parseFloat(balance) || 0).toFixed(2)}`,
    },
    {
      title: '服务器数',
      dataIndex: 'server_count',
      key: 'server_count',
      width: 100,
    },
    {
      title: '注册时间',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 150,
      render: (date: string) => new Date(date).toLocaleString(),
    },
    {
      title: '操作',
      key: 'action',
      width: 200,
      render: (_: any, record: User) => (
        <div className="space-x-2">
          <Button
            size="small"
            onClick={() => {
              setSelectedUser(record);
              setRechargeModalVisible(true);
            }}
          >
            充值
          </Button>
          
          {record.status === 'active' ? (
            <Button
              size="small"
              danger
              onClick={() => {
                confirm({
                  title: '确认暂停用户？',
                  content: `确定要暂停用户 ${record.username} 吗？`,
                  onOk: () => handleStatusChange(record.id, 'suspended'),
                });
              }}
            >
              暂停
            </Button>
          ) : (
            <Button
              size="small"
              type="primary"
              onClick={() => handleStatusChange(record.id, 'active')}
            >
              激活
            </Button>
          )}
        </div>
      ),
    },
  ];

  return (
    <div className="fade-in">
      <Card>
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold">用户管理</h2>
        </div>

        <div className="flex gap-4 mb-6">
          <Search
            placeholder="搜索用户名或邮箱"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="flex-1"
            allowClear
          />
          <Select
            placeholder="用户状态"
            value={statusFilter}
            onChange={setStatusFilter}
            className="w-32"
            allowClear
          >
            <Option value="active">正常</Option>
            <Option value="suspended">暂停</Option>
            <Option value="banned">封禁</Option>
          </Select>
        </div>

        <Table
          columns={columns}
          dataSource={users}
          rowKey="id"
          loading={loading}
          pagination={{
            pageSize: 20,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total) => `共 ${total} 个用户`,
          }}
          scroll={{ x: 1000 }}
        />
      </Card>

      {/* 充值模态框 */}
      <Modal
        title={`为用户 ${selectedUser?.username} 充值`}
        open={rechargeModalVisible}
        onCancel={() => {
          setRechargeModalVisible(false);
          setSelectedUser(null);
        }}
        footer={null}
      >
        <Form onFinish={handleRecharge} layout="vertical">
          <Form.Item
            name="amount"
            label="充值金额"
            rules={[{ required: true, message: '请输入充值金额' }]}
          >
            <InputNumber
              min={0.01}
              max={10000}
              step={0.01}
              placeholder="输入充值金额"
              className="w-full"
              addonAfter="元"
            />
          </Form.Item>

          <Form.Item
            name="note"
            label="备注"
            rules={[{ required: true, message: '请输入备注' }]}
          >
            <Input.TextArea placeholder="输入充值备注" />
          </Form.Item>

          <div className="flex justify-end space-x-2">
            <Button onClick={() => {
              setRechargeModalVisible(false);
              setSelectedUser(null);
            }}>
              取消
            </Button>
            <Button type="primary" htmlType="submit">
              确认充值
            </Button>
          </div>
        </Form>
      </Modal>
    </div>
  );
};

export default Users;



